/**
 * A porta entre a tela de medições e a API do módulo 14.
 */
import { pedir } from '@/lib/pedido';

/**
 * As quatro situações da medição, na grafia do banco.
 *
 * NÃO SÃO os seis status do manual (p.19) — aqueles descrevem uma máquina, e
 * `Operando` não quer dizer nada sobre uma medição. A grafia escrita de cada
 * uma está em `situacaoEscrita`, em `receita.ts`.
 */
export type SituacaoDaMedicao = 'aberta' | 'em conferencia' | 'aprovada' | 'cancelada';

export interface Medicao {
  readonly id: string;
  readonly contratoId: string;
  readonly contrato: string;
  readonly cliente: string;
  /** `aaaa-mm-01`: a competência é um mês, e `formatarCompetencia` a escreve. */
  readonly competencia: string;
  readonly situacao: SituacaoDaMedicao;
  readonly aprovadaEm: string | null;
  readonly valor: number;
  readonly horasReconhecidas: number;
  readonly equipamentos: number;
  /* O id E o numero: a janela de recebimento precisa do id, e a lista mostra
     o numero. Sem o id aqui a tela teria de inventar um, ou a acao ficaria
     desligada so por falta de dado. */
  readonly documentoId: string | null;
  readonly documento: string | null;
  readonly recebidoEm: string | null;
}

export interface RegraDoContrato {
  readonly id: string;
  readonly equipamentoId: string | null;
  readonly equipamento: string | null;
  readonly vigenciaInicio: string;
  readonly vigenciaFim: string | null;
  readonly valorHora: number;
  readonly franquiaHorasMes: number | null;
  readonly valorHoraExcedente: number | null;
  readonly valorHoraEspera: number | null;
  readonly valorMobilizacao: number | null;
  readonly observacao: string;
}

export interface LinhaDeMargem {
  readonly contratoId: string;
  readonly contrato: string;
  readonly cliente: string;
  readonly competencia: string;
  readonly receita: number;
  readonly horasReconhecidas: number;
  readonly equipamentos: number;
  /* 0096 · a margem, pela mesma conta da Visão do gestor. Nulos sem custo.ler
     ou em competência mais antiga que as apuradas; ausentes em servidor anterior. */
  readonly custoTotal?: number | null;
  readonly resultado?: number | null;
  /** Em pontos percentuais; nula sem receita. */
  readonly margem?: number | null;
  /** Nada ficou fora na competência: aí é "Margem", e não "Margem parcial". */
  readonly completo?: boolean;
}

export const api = {
  medicoes: () => pedir<{ medicoes: readonly Medicao[] }>('/receita/medicoes'),
  regras: (contratoId: string) =>
    pedir<{ regras: readonly RegraDoContrato[] }>(`/receita/contratos/${encodeURIComponent(contratoId)}/regras`),
  /** A margem vem com a limitação escrita junto do número, e a tela a mostra. */
  margem: () => pedir<{ linhas: readonly LinhaDeMargem[]; limitacao: string }>('/receita/margem'),

  montar: (contratoId: string, competencia: string) =>
    pedir<{ id: string; itens: number }>('/receita/medicoes', {
      method: 'POST',
      body: JSON.stringify({ contratoId, competencia }),
    }),
  aprovar: (id: string) =>
    pedir<{ id: string }>(`/receita/medicoes/${encodeURIComponent(id)}/aprovacao`, { method: 'POST' }),
  cancelar: (id: string, motivo: string) =>
    pedir<{ id: string }>(`/receita/medicoes/${encodeURIComponent(id)}/cancelamento`, {
      method: 'POST',
      body: JSON.stringify({ motivo }),
    }),
  /* 0109: com o ERP mestre da cobrança, `contingencia` é o motivo da escrita
     em contingência (só para quem tem `integracao.contingencia`). */
  emitir: (id: string, numero: string, emitidoEm: string, contingencia?: string) =>
    pedir<{ id: string; emContingencia?: boolean }>(`/receita/medicoes/${encodeURIComponent(id)}/documentos`, {
      method: 'POST',
      body: JSON.stringify({ numero, emitidoEm, ...(contingencia ? { contingencia } : {}) }),
    }),
  receber: (documentoId: string, recebidoEm: string, valorRecebido: number, contingencia?: string) =>
    pedir<{ id: string; emContingencia?: boolean }>(`/receita/documentos/${encodeURIComponent(documentoId)}/recebimento`, {
      method: 'POST',
      body: JSON.stringify({ recebidoEm, valorRecebido, ...(contingencia ? { contingencia } : {}) }),
    }),
};
